export const FAULT_LABELS = {
  DRY_RUN: 'Possible Dry Run',
  LOW_PF: 'Low Power Factor',
  PHASE_CURRENT_IMBALANCE: 'Phase Current Imbalance',
  VOLTAGE_IMBALANCE: 'Voltage Imbalance',
  PHASE_LOSS: 'Possible Phase Loss',
  VOLTAGE_PHASE_LOSS: 'Possible Voltage Phase Loss',
  UNDER_VOLTAGE: 'Under Voltage',
  OVER_VOLTAGE: 'Over Voltage',
  MOTOR_OVERLOAD: 'Possible Motor Overload',
  FLOW_RESTRICTION: 'Possible Flow Restriction',
  SHORT_CYCLING: 'Short Cycling',
};

const STATE_LABELS = {
  NORMAL: 'Normal',
  OFF: 'Pump Off',
  WARMUP: 'Waiting for Stable Data',
  BASELINE_PENDING: 'Building Baseline',
  BASELINE_INCOMPLETE: 'Building Baseline',
  DATA_QUALITY_ERROR: 'Data Quality Error',
  DATA_ERROR: 'Data Unavailable',
  MODEL_ERROR: 'Analysis Unavailable',
  MODEL_SCORE_UNAVAILABLE: 'Waiting for Analysis',
};

export function stateOf(payload) {
  if (!payload) return 'UNKNOWN';
  return String(payload.state || 'UNKNOWN').toUpperCase();
}

export function namedFaults(payload) {
  if (!payload || !Array.isArray(payload.detected_faults)) return [];
  return payload.detected_faults.map((fault) => {
    const code = String(fault.code || '').toUpperCase();
    return {
      ...fault,
      code,
      displayLabel: fault.label || FAULT_LABELS[code] || code.replaceAll('_', ' ') || 'Returned issue',
    };
  });
}

export function conditionLabel(payload) {
  if (!payload) return 'Waiting for selection';
  const state = stateOf(payload);
  if (state === 'ANOMALY') {
    const faults = namedFaults(payload);
    if (faults.length === 1) return faults[0].displayLabel;
    if (faults.length > 1) return 'Multiple issues detected';
    return 'Unusual Pump Behaviour';
  }
  return STATE_LABELS[state] || `Unknown state: ${state}`;
}

export function conditionTone(payload) {
  if (!payload) return 'unknown';
  const state = stateOf(payload);
  if (state === 'NORMAL') return 'normal';
  if (state === 'ANOMALY') return 'anomaly';
  if (state === 'OFF') return 'off';
  if (['WARMUP', 'BASELINE_PENDING', 'BASELINE_INCOMPLETE', 'MODEL_SCORE_UNAVAILABLE'].includes(state)) return 'warning';
  if (['DATA_QUALITY_ERROR', 'DATA_ERROR', 'MODEL_ERROR'].includes(state)) return 'error';
  return 'unknown';
}

export function statusMessage(payload) {
  if (!payload) return 'Choose a pump to begin monitoring.';
  const state = stateOf(payload);
  if (state === 'NORMAL') {
    if ((payload.early_warnings || []).length > 0) {
      return 'The pump is operating, but an early change needs attention.';
    }
    return 'No current issue detected.';
  }
  if (state === 'OFF') return 'The pump is off. Monitoring will resume when it starts.';
  if (state === 'WARMUP') return 'Readings are settling after startup.';
  if (state === 'MODEL_SCORE_UNAVAILABLE') return 'The system is waiting for enough information to complete analysis.';
  if (state === 'BASELINE_PENDING' || state === 'BASELINE_INCOMPLETE') {
    const detail = payload.baseline_pending_detail;
    const reason = detail && typeof detail.reason === 'string' ? detail.reason : null;
    return reason || 'This pump is still learning its usual operating range.';
  }
  if (state === 'DATA_QUALITY_ERROR') return 'The latest reading contains missing or invalid sensor data.';
  if (state === 'DATA_ERROR') return 'Fresh pump data could not be retrieved.';
  if (state === 'MODEL_ERROR') return 'The current reading could not be analysed.';
  if (state === 'ANOMALY') {
    const faults = namedFaults(payload);
    if (faults.length === 0) return 'One or more readings changed significantly from this pump\'s usual operation.';
    if (faults.length === 1) return faults[0].evidence || 'The backend returned this candidate issue.';
    return 'The backend returned more than one candidate issue for this reading.';
  }
  return payload.summary?.message || 'The backend returned an unrecognised condition.';
}
